/**
 * Sefaria Text Service
 *
 * Provides API access to Sefaria's text database with:
 * - Cache-first data fetching
 * - Request deduplication
 * - Response parsing and sanitization
 * - Error handling with fallback to cache
 * - Timeout management
 *
 * Design Principle: Components should NEVER call fetch directly.
 * All data access goes through this service which uses the cache layer.
 */

import {
  RateLimitError,
  ApiError,
  CACHE_TTL,
  type SefariaText,
  type SefariaVerse,
  type SefariaLink,
  type SefariaVersion,
  type SefariaTextApiResponse,
  type SefariaLinkApiResponse,
  type SefariaVersionApiResponse,
} from '@/types/sefaria';
import {
  getCachedText,
  setCachedText,
  getCachedCommentary,
  setCachedCommentary,
  getCachedVersions,
  setCachedVersions,
  fetchWithDedup,
  getImmediateText,
  isTextAvailableOffline,
} from './sefariaCache';

// ============================================================
// Configuration
// ============================================================

const SEFARIA_API_BASE = 'https://www.sefaria.org/api';
const DEFAULT_TIMEOUT = 15000; // 15 seconds
const COMMENTARY_TIMEOUT = 10000; // 10 seconds

// ============================================================
// Fetch Utilities
// ============================================================

/**
 * Fetch with timeout using AbortController
 */
async function fetchWithTimeout(
  url: string,
  options: { timeout?: number } = {}
): Promise<Response> {
  const controller = new AbortController();
  const timeout = options.timeout || DEFAULT_TIMEOUT;

  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, { signal: controller.signal });

    // Handle rate limiting
    if (response.status === 429) {
      throw new RateLimitError('Sefaria API rate limited');
    }

    if (!response.ok) {
      throw new ApiError(`API error: ${response.status}`, response.status);
    }

    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ============================================================
// Text Sanitization
// ============================================================

/**
 * Sanitize Hebrew text from Sefaria API
 * Removes HTML entities and formatting artifacts
 */
function sanitizeHebrewText(text: string | string[] | null | undefined): string {
  if (!text) return '';
  // Handle arrays (Sefaria API sometimes returns nested arrays)
  if (Array.isArray(text)) {
    return text.map((t) => sanitizeHebrewText(t)).join(' ');
  }
  if (typeof text !== 'string') return '';

  return (
    text
      // Remove footnote/annotation blocks (if any in Hebrew text)
      .replace(/<i[^>]*class\s*=\s*["']footnote["'][^>]*>[\s\S]*?<\/i>/gi, '')
      .replace(/<sup[^>]*>[\s\S]*?<\/sup>/gi, '')
      // Remove HTML entities
      .replace(/&thinsp;/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#x27;/g, "'")
      .replace(/&quot;/g, '"')
      // Remove all HTML tags (bounded to prevent ReDoS on malformed input)
      .replace(/<[^>]{0,1000}>/g, '')
      // Normalize whitespace
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * Sanitize English text from Sefaria API
 * Removes all HTML tags including footnotes, which appear as:
 * <i class="footnote"><b>word </b>explanation text</i>
 */
function sanitizeEnglishText(text: string | string[] | null | undefined): string {
  if (!text) return '';
  // Handle arrays (Sefaria API sometimes returns nested arrays)
  if (Array.isArray(text)) {
    return text.map((t) => sanitizeEnglishText(t)).join(' ');
  }
  if (typeof text !== 'string') return '';

  return (
    text
      // Remove footnote blocks entirely (including their content)
      // Footnotes appear as: <i class="footnote">...</i> or similar
      .replace(/<i[^>]*class\s*=\s*["']footnote["'][^>]*>[\s\S]*?<\/i>/gi, '')
      // Remove any remaining footnote-style annotations (superscript letters, etc.)
      .replace(/<sup[^>]*>[\s\S]*?<\/sup>/gi, '')
      // Remove HTML entities
      .replace(/&nbsp;/g, ' ')
      .replace(/&thinsp;/g, '')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#x27;/g, "'")
      .replace(/&quot;/g, '"')
      // Remove all remaining HTML tags (bounded to prevent ReDoS on malformed input)
      .replace(/<[^>]{0,1000}>/g, '')
      // Normalize whitespace
      .replace(/\s+/g, ' ')
      .trim()
  );
}

// ============================================================
// Reference Parsing
// ============================================================

/**
 * Extract the starting verse number from a Sefaria reference
 * e.g., "Exodus 13:17-17:16" -> { chapter: 13, verse: 17 }
 */
function extractStartPosition(ref: string): { chapter: number; verse: number } {
  // Handle refs like "Exodus 13:17-17:16" or "Exodus 13:17"
  // eslint-disable-next-line sonarjs/slow-regex -- Linear: digit character class only
  const match = ref.match(/(\d+):(\d+)/);
  if (match) {
    return {
      chapter: parseInt(match[1], 10),
      verse: parseInt(match[2], 10),
    };
  }

  // Handle chapter-only refs like "Exodus 13"
  const chapterMatch = ref.match(/\s(\d+)$/);
  if (chapterMatch) {
    return {
      chapter: parseInt(chapterMatch[1], 10),
      verse: 1,
    };
  }

  return { chapter: 1, verse: 1 };
}

/**
 * Extract parsha name from a Sefaria URL reference
 * e.g., "Exodus.13.17-17.16" -> "Exodus 13:17-17:16"
 */
export function normalizeRef(urlRef: string): string {
  // Convert URL format (dots) to standard format (spaces and colons)
  // eslint-disable-next-line sonarjs/slow-regex -- Linear: digit character class only
  return urlRef.replace(/\./g, ' ').replace(/(\d+) (\d+)/g, '$1:$2');
}

/**
 * Extract book name from reference
 * e.g., "Exodus 13:17-17:16" -> "Exodus"
 */
function extractBook(ref: string): string {
  const match = ref.match(/^([A-Za-z\s]+)/);
  return match ? match[1].trim() : '';
}

// ============================================================
// Response Parsing
// ============================================================

/**
 * Flatten nested arrays from Sefaria API response
 * Sefaria returns nested arrays for multi-chapter sections:
 * [[ch1 verses], [ch2 verses]] -> [all verses flat]
 */
function flattenTextArray(arr: unknown): string[] {
  // Check string first (empty string is falsy but valid)
  if (typeof arr === 'string') return [arr];
  // Now safe to check for null/undefined
  if (arr == null) return [];
  if (!Array.isArray(arr)) return [];

  const result: string[] = [];
  for (const item of arr) {
    if (typeof item === 'string') {
      result.push(item);
    } else if (Array.isArray(item)) {
      // Recursively flatten nested arrays
      result.push(...flattenTextArray(item));
    }
  }
  return result;
}

/**
 * Parse the raw Sefaria text API response into our data structure
 */
function parseTextResponse(response: SefariaTextApiResponse, parshaName?: string): SefariaText {
  const verses: SefariaVerse[] = [];

  // Flatten nested arrays (Sefaria returns nested arrays for multi-chapter sections)
  const textArray = flattenTextArray(response.text);
  const heArray = flattenTextArray(response.he);

  // Get starting position
  const startPos = extractStartPosition(response.ref);
  const currentChapter = startPos.chapter;
  let currentVerse = startPos.verse;

  for (let i = 0; i < Math.max(textArray.length, heArray.length); i++) {
    const hebrewText = heArray[i] || '';
    const englishText = textArray[i] || '';

    // Skip empty verses
    if (!hebrewText && !englishText) {
      currentVerse++;
      continue;
    }

    const verseRef = `${extractBook(response.ref)} ${currentChapter}:${currentVerse}`;

    verses.push({
      ref: verseRef,
      heRef: response.heRef || '',
      verseNumber: currentVerse,
      chapter: currentChapter,
      hebrew: sanitizeHebrewText(hebrewText),
      english: sanitizeEnglishText(englishText),
      hasCommentary: true, // Assume true, actual check on expand
    });

    currentVerse++;
  }

  // Parse available versions
  const availableVersions: SefariaVersion[] = (response.versions || [])
    .filter((v: SefariaVersionApiResponse) => v.language === 'en')
    .map((v: SefariaVersionApiResponse) => ({
      versionTitle: v.versionTitle,
      shortVersionTitle: v.shortVersionTitle || v.versionTitle,
      language: v.language,
      isPrimary: v.isPrimary || false,
      license: v.license,
    }));

  return {
    ref: response.ref,
    book: response.indexTitle || extractBook(response.ref),
    parsha: parshaName || extractBook(response.ref),
    verses,
    availableVersions,
    fetchedAt: new Date().toISOString(),
  };
}

/**
 * Parse the raw Sefaria links API response
 */
function parseLinksResponse(response: SefariaLinkApiResponse[]): SefariaLink[] {
  if (!Array.isArray(response)) return [];

  return response
    .filter((link) => {
      // Only include links that have English content or are commentaries
      return link.category === 'Commentary' || link.sourceHasEn;
    })
    .map((link) => ({
      ref: link.ref,
      category: link.category,
      collectiveTitle: link.collectiveTitle || { en: '', he: '' },
      hebrew: sanitizeHebrewText(link.he || ''),
      english: sanitizeEnglishText(link.text || ''),
      anchorRef: link.anchorRef,
    }))
    .filter((link) => link.hebrew || link.english); // Only keep links with content
}

/**
 * Parse version list from API response
 */
function parseVersionsResponse(response: SefariaVersionApiResponse[]): SefariaVersion[] {
  if (!Array.isArray(response)) return [];

  return response
    .filter((v) => v.language === 'en')
    .sort((a, b) => (b.priority || 0) - (a.priority || 0))
    .map((v) => ({
      versionTitle: v.versionTitle,
      shortVersionTitle: v.shortVersionTitle || v.versionTitle,
      language: v.language,
      isPrimary: v.isPrimary || false,
      license: v.license,
    }));
}

// ============================================================
// Public API - Text
// ============================================================

/**
 * Get parsha text - cache-first with fallback
 *
 * @param parshaRef - Sefaria reference (e.g., "Exodus.13.17-17.16")
 * @param options - Optional settings
 * @returns The parsha text data
 */
export async function getParshaText(
  parshaRef: string,
  options?: { forceRefresh?: boolean; parshaName?: string }
): Promise<SefariaText> {
  const normalizedRef = normalizeRef(parshaRef);
  const cacheKey = `sefaria_text_${normalizedRef}`;

  // 1. Return from cache if valid (unless forceRefresh)
  if (!options?.forceRefresh) {
    const cached = getCachedText(normalizedRef);
    if (cached) {
      return cached;
    }
  }

  // 2. Fetch via deduplication layer
  try {
    return await fetchWithDedup(
      cacheKey,
      async () => {
        const url = `${SEFARIA_API_BASE}/texts/${encodeURIComponent(normalizedRef)}?context=0`;
        const response = await fetchWithTimeout(url, { timeout: DEFAULT_TIMEOUT });
        const data = (await response.json()) as SefariaTextApiResponse;
        const parsed = parseTextResponse(data, options?.parshaName);

        // Also cache it directly
        setCachedText(normalizedRef, parsed);

        return parsed;
      },
      CACHE_TTL.TEXT
    );
  } catch (error) {
    // On error, try to return stale cache
    const staleCache = getCachedText(normalizedRef, { ignoreExpiry: true });
    if (staleCache) {
      console.warn('Using stale cache due to API error:', error);
      return staleCache;
    }
    throw error;
  }
}

/**
 * Get parsha text synchronously from cache (for instant UI)
 * Returns null if not cached
 */
export function getImmediateParshaText(parshaRef: string): SefariaText | null {
  const normalizedRef = normalizeRef(parshaRef);
  return getImmediateText(normalizedRef);
}

/**
 * Check if parsha text is available offline
 */
export function isParshaAvailableOffline(parshaRef: string): boolean {
  const normalizedRef = normalizeRef(parshaRef);
  return isTextAvailableOffline(normalizedRef);
}

// ============================================================
// Public API - Commentary
// ============================================================

/**
 * Get commentary for a verse - lazy loaded, cached
 *
 * @param verseRef - Verse reference (e.g., "Exodus 13:17")
 * @returns Array of commentary links
 */
export async function getVerseCommentary(verseRef: string): Promise<SefariaLink[]> {
  const cacheKey = `sefaria_links_${verseRef}`;

  // Check cache first
  const cached = getCachedCommentary(verseRef);
  if (cached) {
    return cached;
  }

  // Fetch via deduplication layer
  return fetchWithDedup(
    cacheKey,
    async () => {
      const url = `${SEFARIA_API_BASE}/links/${encodeURIComponent(verseRef)}`;
      const response = await fetchWithTimeout(url, { timeout: COMMENTARY_TIMEOUT });
      const data = (await response.json()) as SefariaLinkApiResponse[];
      const parsed = parseLinksResponse(data);

      // Cache the result
      setCachedCommentary(verseRef, parsed);

      return parsed;
    },
    CACHE_TTL.COMMENTARY
  );
}

// ============================================================
// Public API - Versions
// ============================================================

/**
 * Get available translation versions for a book
 *
 * @param book - Book name (e.g., "Exodus")
 * @returns Array of available versions
 */
export async function getAvailableVersions(book: string): Promise<SefariaVersion[]> {
  const cacheKey = `sefaria_versions_${book}`;

  // Check cache first
  const cached = getCachedVersions(book);
  if (cached) {
    return cached;
  }

  // Fetch via deduplication layer
  return fetchWithDedup(
    cacheKey,
    async () => {
      const url = `${SEFARIA_API_BASE}/texts/versions/${encodeURIComponent(book)}`;
      const response = await fetchWithTimeout(url, { timeout: COMMENTARY_TIMEOUT });
      const data = (await response.json()) as SefariaVersionApiResponse[];
      const parsed = parseVersionsResponse(data);

      // Cache the result
      setCachedVersions(book, parsed);

      return parsed;
    },
    CACHE_TTL.VERSIONS
  );
}

// ============================================================
// Pre-warming
// ============================================================

/**
 * Pre-warm the cache for a parsha (background fetch)
 * Uses requestIdleCallback for non-blocking operation
 *
 * @param parshaRef - Sefaria reference
 */
export function prewarmParshaCache(parshaRef: string): void {
  const normalizedRef = normalizeRef(parshaRef);

  // Skip if already cached
  if (isTextAvailableOffline(normalizedRef)) {
    return;
  }

  // Use requestIdleCallback if available, otherwise setTimeout
  const schedulePrewarm = () => {
    getParshaText(parshaRef).catch(() => {
      // Silent fail - will fetch on-demand if needed
    });
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(schedulePrewarm, { timeout: 5000 });
  } else {
    setTimeout(schedulePrewarm, 1000);
  }
}
